import React, { useState, useEffect, useRef } from 'react'

import {
  Form,
  Input,
  ModalProps,
  FormProps,
  Modal,
  InputNumber,
  Space,
  Button,
  Tag,
  Row,
  Col,
  Select,
  Divider,
  useSelect,
  Descriptions,
  Breadcrumb,
  Typography,
} from '@pankod/refine-antd'
import { Upload, message } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { useGetIdentity, useOne, useTable } from '@pankod/refine-core'
import ReactMarkdown from 'react-markdown'
import ReactMde from 'react-mde'
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { ICentaConcept, ICompetency, IExplanationTypes, IStrand, ITNTaxonomy } from 'interfaces'

import 'react-mde/lib/styles/css/react-mde-all.css'
import {
  IGrade,
  ISubject,
  ITopic,
  IBloomLevel,
  ITeacherLevel,
  IDifficultyLevel,
  IQuestion,
  IQuestionType,
  IWorkRequest,
  IReportTheme,
  ISubTheme,
  ITheme,
  IAspect,
} from 'interfaces'
import { type } from 'os'
import { nhost } from 'utility'
import wr_metadata from 'metadata/workrequests'
import { CompletionStatus, getWorkRequestStatusCount } from 'helpers'
import {
  createMailForApproval,
  createMailForReview,
  createMailForReviewRequireAmending,
  createMailOpenRequireAmending,
  createMailRejectedAtApproval,
  createMailRejectedAtRewiew,
} from 'helpers'
import {
  createLogForApproval,
  createLogForReview,
  createLogForReviewRequireAmending,
  createLogOpenRequireAmending,
  createLogRejectedAtApproval,
  createLogRejectedAtRewiew,
  createNotificationForApproval,
  createNotificationForReview,
  createNotificationForReviewRequireAmending,
  createNotificationOpenRequireAmending,
  createNotificationRejectedAtApproval,
  createNotificationRejectedAtRewiew,
} from 'gql/mutations'
import { ReactMdeToolbarCommands } from 'helpers/markdownConfig'
import { useUserContext } from 'context/user'
import { Editor } from '@tinymce/tinymce-react'
// import RichTextEditor from 'components/questions/rich_text_editor'
import SunTextEditor from 'components/questions/sun_text_editor'

type EditQuestionProps = {
  modalProps: ModalProps
  formProps: FormProps
  record: any
  close: any
  updatePercentage?: any
}

type BlobInfo = {
  id: () => string
  name: () => string
  filename: () => string
  blob: () => Blob
  base64: () => string
  blobUri: () => string
  uri: () => string | undefined
}

type ProgressFn = (percent: number) => void

type UploadHandler = (blobInfo: BlobInfo, progress: ProgressFn) => Promise<string>

const { TextArea } = Input

export const EditDraftQuestion: React.FC<EditQuestionProps> = ({ modalProps, formProps, record, close, updatePercentage }) => {
  const [questionStatus, setQuestionStatus] = useState<string>('')
  const { data: user } = useGetIdentity()
  const { qcreatorList, qreviewerList, qapproverList, vcreatorList, userRole } = useUserContext()
  const role = user?.defaultRole
  const [selectedTab, setSelectedTab] = useState<'write' | 'preview'>('write')
  const [questionType, setQuestionType] = useState('MCQ')
  const [optionType, setOptionType] = useState('Text')
  const [reviewers, setReviewers] = useState<string[]>([])
  const [approvers, setApprovers] = useState<string[]>([])
  const [taxonomyfilter, setTaxonomyFilter] = useState<any[]>([])
  const [strandfilter, setStrandFilter] = useState<any[]>([])
  const [errorOption, setError] = useState<boolean>(false)
  let isClose: boolean = false

  const [selectedItems, setSelectedItems] = useState<string>('')

  const editorRef = useRef<Editor>(null)

  const { data: workRequestRecord } = useOne<IWorkRequest>({
    resource: 'work_requests',
    id: record?.work_request_id,
    metaData: wr_metadata,
    queryOptions: {
      enabled: !!record?.work_request_id && !!modalProps?.visible,
    },
  })

  useEffect(() => {
    if (workRequestRecord?.data) setReviewers(workRequestRecord?.data?.reviewer_ids ?? [])
  }, [workRequestRecord])

  useEffect(() => {
    if (workRequestRecord?.data && record) {
      const approverIds = workRequestRecord?.data?.approver_ids?.filter((list: any) => list?.reviwer_id_selected === record?.reviewer_id)
      if (approverIds && approverIds[0]) setApprovers(approverIds[0]?.approver_ids ?? [])
    }
  }, [workRequestRecord, record])

  //file upload start ------------------------------
  const [fileList, setFileList] = useState<any>({})
  const [uploading, setUploading] = useState(false)

  const handleImageUpload = async () => {
    setUploading(true)
    let metadatas: any = []
    for (const property in fileList) {
      const file = fileList[property][0]
      if (file) {
        const { fileMetadata } = await nhost.storage.upload({ file })
        metadatas.push(fileMetadata)
      } else {
        metadatas.push([])
      }
    }
    setUploading(false)
    return metadatas
  }

  const handleOptionTags = (value: string) => {
    if (questionType === 'MCQ') {
      const field = formProps?.form?.getFieldValue('option_details')
      const found = field?.findIndex((element: any) => element?.option_flag === 'Right Answer')
      if (found === -1) {
        setSelectedItems('')
      } else {
        setSelectedItems('Right Answer')
      }
    }
  }

  const option_tags = ['Right Answer', 'Most Wrong', 'Close to Correct']
  const filteredOptions = option_tags?.filter(o => selectedItems !== o)
  //file upload end ---------------------------

  // list of grades from question
  // const { data: gradeList } = useOne<IQuestion>({
  //   resource: 'questions',
  //   id: record?.id!,
  //   metaData: { fields: ['grade_ids'] },
  //   queryOptions: {
  //     enabled: !!record?.id && !!modalProps?.visible,
  //   },
  // })
  const gradesToPush = record?.grade_ids

  const { queryResult: gradeQueryResult } = useSelect<IGrade>({
    resource: 'grades',
    optionLabel: 'name',
    optionValue: 'id',
    metaData: {
      fields: ['id', 'name'],
    },
    sort: [{ field: 'name', order: 'asc' }],
    filters: [
      {
        field: 'id',
        operator: 'in',
        value: gradesToPush,
      },
    ],
    onSearch: value => [
      {
        field: 'name',
        operator: 'contains',
        value: `${value.toLowerCase()}%`,
      },
    ],
    queryOptions: { enabled: !!record?.id && !!modalProps?.visible },
  })

  const handleOptionType = (value: any) => {
    setOptionType(value)
  }

  const handleSetFormList = (content: string, key: number, fieldName: string) => {
    const optionsArr = formProps?.form?.getFieldValue('option_details')
    console.log({ optionsArr })
    if (optionsArr?.[key] === undefined) {
      optionsArr[key] = { [fieldName]: content }
    }
    const formVal = optionsArr?.[key]
    Object.assign(formVal, { ...formVal, [fieldName]: content })
    formProps?.form?.setFieldsValue({ option_details: optionsArr })
  }

  const handleImageUplodFromEditor: UploadHandler = async (blobInfo, progress) => {
    let url = ''
    try {
      const fileName = blobInfo?.filename()
      const blob = blobInfo?.blob()
      const file = new File([blob], fileName)
      const { fileMetadata } = await nhost.storage.upload({ file })
      const fileId = fileMetadata?.id ?? ''
      url = await nhost.storage.getPublicUrl({ fileId })
    } catch (err) {
      console.log({ err })
    }
    return Promise.resolve(url)
  }

  const handleOnFinish = async (status: string) => {
    try {
      const validate = await formProps.form?.validateFields()
      setQuestionStatus(status)
      if (status === 'Open (Require Amending)') {
        let creatorDetails = []
        if (workRequestRecord?.data?.request_type === 'Q') {
          creatorDetails = qcreatorList.filter((item: any) => item?.id === record?.creator_id)[0]
        }
        if (workRequestRecord?.data?.request_type === 'V') {
          creatorDetails = vcreatorList.filter((item: any) => item?.id === record?.creator_id)[0]
        }
        record && createNotificationOpenRequireAmending(user?.id, record?.creator_id, workRequestRecord?.data, record?.question_id)
        record && createNotificationOpenRequireAmending(user?.id, record?.creator_id, workRequestRecord?.data, record?.question_id)
        record && createLogOpenRequireAmending(record?.reviewer_id, record?.creator_id, workRequestRecord?.data, record?.id)
        createMailOpenRequireAmending(workRequestRecord?.data, record?.question_id, creatorDetails?.user?.displayName, creatorDetails?.user?.email)
      }
      if (status === 'For Review (Require Amending)') {
        let reviewerDetails = qreviewerList.filter((reviewers: any) => reviewers?.id === record?.reviewer_id)[0]

        record && createNotificationForReviewRequireAmending(user?.id, record?.reviewer_id, workRequestRecord?.data, record?.question_id)
        record && createNotificationForReviewRequireAmending(user?.id, record?.reviewer_id, workRequestRecord?.data, record?.question_id)
        record && createLogForReviewRequireAmending(record?.approver_id, record?.reviewer_id, workRequestRecord?.data, record?.id)
        createMailForReviewRequireAmending(
          workRequestRecord?.data,
          record?.question_id,
          reviewerDetails?.user?.displayName,
          reviewerDetails?.user?.email
        )
      }
      if (status === 'Rejected at Review') {
        let creatorDetails = []
        if (workRequestRecord?.data?.request_type === 'Q') {
          creatorDetails = qcreatorList.filter((item: any) => item?.id === record?.creator_id)[0]
        }
        if (workRequestRecord?.data?.request_type === 'V') {
          creatorDetails = vcreatorList.filter((item: any) => item?.id === record?.creator_id)[0]
        }
        record && createNotificationRejectedAtRewiew(user?.id, record?.creator_id, workRequestRecord?.data, record?.question_id)
        record && createNotificationRejectedAtRewiew(user?.id, record?.creator_id, workRequestRecord?.data, record?.question_id)
        record && createLogRejectedAtRewiew(record?.reviewer_id, workRequestRecord?.data, record?.id)
        createMailRejectedAtRewiew(workRequestRecord?.data, record?.question_id, creatorDetails?.user?.displayName, creatorDetails?.user?.email)
      }
      if (status === 'Rejected at Approval') {
        let reviewerDetails = qreviewerList.filter((reviewers: any) => reviewers?.id === record?.reviewer_id)[0]

        record &&
          createNotificationRejectedAtApproval(user?.id, record?.creator_id, record?.reviewer_id, workRequestRecord?.data, record?.question_id)
        record &&
          createNotificationRejectedAtApproval(user?.id, record?.creator_id, record?.reviewer_id, workRequestRecord?.data, record?.question_id)
        record && createLogRejectedAtApproval(record?.approver_id, workRequestRecord?.data, record?.id)
        createMailRejectedAtApproval(workRequestRecord?.data, record?.question_id, reviewerDetails?.user?.displayName, reviewerDetails?.user?.email)
      }
      formProps?.form?.submit()
    } catch (error) {
      console.log(error)
    }
  }

  const onFinish = async (values: any) => {
    let option_array: any = []
    let is_ra_found = false
    let reviewerId: any = null
    let approverId: any = null
    if (userRole === 'qcreator' || userRole === 'variantcreator') {
      if (questionStatus === 'For Review') {
        if (record?.reviewer_id !== null && record?.reviewer_id !== '') {
          reviewerId = record?.reviewer_id
        } else {
          if (JSON.stringify(workRequestRecord?.data?.reviewer_ids) !== '[]') {
            // let currentCount = workRequestRecord?.data?.question_reviewer_approver?.length
            // if (currentCount === 0) {
            const { for_review_count, reviewed_count, approved_count, for_approval_count, rejected_count, retired_count } = getWorkRequestStatusCount(
              workRequestRecord?.data
            )
            const reviewerIndex =
              (for_review_count + reviewed_count + rejected_count + retired_count + approved_count + for_approval_count) % reviewers?.length
            // console.log('reviewerIndex', reviewerIndex)
            reviewerId = workRequestRecord?.data?.reviewer_ids[reviewerIndex]
            // }
          }
        }
      }
    } else if (userRole === 'qreviewer') {
      reviewerId = record?.reviewer_id

      if (questionStatus === 'For Approval') {
        if (record?.approver_id !== null && record?.approver_id !== '') {
          approverId = record?.approver_id
        } else {
          // if (record?.reviewer_id === user?.id) {
          if (JSON.stringify(workRequestRecord?.data?.workRequestApprovers) !== '[]') {
            const { for_approval_count, approved_count, rejected_count, retired_count } = getWorkRequestStatusCount(workRequestRecord?.data)
            const approverIndex = (for_approval_count + approved_count + rejected_count + retired_count) % approvers?.length

            approverId = approvers[approverIndex]
            // console.log('approverIndex', approverIndex)
            // }
          }
        }
      }
    } else {
      approverId = record?.approver_id
      reviewerId = record?.reviewer_id
      // console.log('bbbbbbbbbbbbbbbbb', reviewerId, approverId)
    }

    if (questionType === 'MCQ' && values?.option_details?.length > 0) {
      // const found = values.option_details?.findIndex((element: any) => element?.option_flag === 'Right Answer')
      // if (found === -1) {
      //   setError(true)
      //   is_ra_found = false
      // } else {
      //   setError(false)
      //   is_ra_found = true
      // }
      values.option_details.forEach((option: any, index: number) => {
        let newOption = { ...option }
        if (option?.option_text.includes('![image]')) {
          const middle = option?.option_text?.slice(option?.option_text.lastIndexOf('/') + 1, option?.option_text?.length - 1)
          // console.log({ middle })
          newOption.option_image_id = middle
          newOption.type = 'Image'
          // newOption.option_image_name = ''
        } else if (option?.option_text?.includes('<img')) {
          let imgTagPattern = /<img.*?>/
          let imgTagMatch = option?.option_text?.match(imgTagPattern)
          let imgTagString = imgTagMatch ? imgTagMatch[0] : ''
          let imgSrc = imgTagString.split('"')?.[1] ?? ''
          let imgId = imgSrc?.slice(imgSrc?.lastIndexOf('/') + 1)
          newOption['option_image_id'] = imgId
          newOption['type'] = 'Image'
          console.log('iddd', imgId, option)
        } else {
          newOption.type = 'Text'
        }
        option_array.push(newOption)
        if (is_ra_found) {
          return
        }
        if (option?.option_flag === 'Right Answer') {
          is_ra_found = true

          return
        } else {
          is_ra_found = false
        }
      })
    } else if (questionType === 'Match' && values?.option_details?.length > 0) {
      option_array = []
      values.option_details.forEach((option: any, index: number) => {
        let newOption = { ...option }
        // LEFT LABEL
        if (option?.left_label_text.includes('![image]')) {
          let left_middle = option?.left_label_text?.slice(option?.left_label_text.lastIndexOf('/') + 1, option?.left_label_text?.length - 1)
          newOption.left_label_image = left_middle
          newOption.type = 'Image'
        } else if (option?.left_label_text?.includes('<img')) {
          let imgTagPattern = /<img.*?>/
          let imgTagMatch = option?.left_label_text?.match(imgTagPattern)
          let imgTagString = imgTagMatch ? imgTagMatch[0] : ''
          let imgSrc = imgTagString.split('"')?.[1] ?? ''
          let imgId = imgSrc?.slice(imgSrc?.lastIndexOf('/') + 1)
          newOption['left_label_image'] = imgId
          newOption['type'] = 'Image'
        } else {
          newOption['type'] = 'Text'
        }
        //RIGHT LABEL
        if (option?.right_label_text.includes('![image]')) {
          let right_middle = option?.right_label_text?.slice(option?.right_label_text.lastIndexOf('/') + 1, option?.right_label_text?.length - 1)
          newOption.right_label_image = right_middle
          newOption.type = 'Image'
        } else if (option?.right_label_text?.includes('<img')) {
          let imgTagPattern = /<img.*?>/
          let imgTagMatch = option?.right_label_text?.match(imgTagPattern)
          let imgTagString = imgTagMatch ? imgTagMatch[0] : ''
          let imgSrc = imgTagString.split('"')?.[1] ?? ''
          let imgId = imgSrc?.slice(imgSrc?.lastIndexOf('/') + 1)
          newOption['right_label_image'] = imgId
          newOption['type'] = 'Image'
        }
        //  else {
        //   newOption.type = 'Text'
        // }
        option_array.push(newOption)
      })
    }
    if (option_array?.length > 0 && questionType === 'MCQ') {
      setError(!is_ra_found)
      isClose = !is_ra_found
      // return true
    }

    // if (values?.option_details?.length > 0 && questionType === 'MCQ') {
    //   // alert(1)
    //   const found = values.option_details?.findIndex((element: any) => element?.option_flag === 'Right Answer')

    //   if (found === -1) {
    //     setError(true)
    //     is_ra_found = false
    //   } else {
    //     setError(false)
    //     is_ra_found = true
    //   }
    // }
    // console.log('xxxxxxxx-------', is_ra_found, errorOption)

    // close()

    if (!uploading) {
      if (questionType === 'MCQ') {
        if (option_array?.length > 0) {
          if (is_ra_found) {
            return (
              formProps.onFinish &&
              formProps.onFinish({
                ...values,
                option_type: '',
                option_details: option_array,
                status: questionStatus,
                reviewer_id: reviewerId,
                approver_id: approverId,
              })
            )
          }
        } else {
          return (
            formProps.onFinish &&
            formProps.onFinish({
              ...values,
              option_type: '',
              option_details: values?.option_details,
              status: questionStatus,
              reviewer_id: reviewerId,
              approver_id: approverId,
            })
          )
        }
      } else if (questionType === 'Match') {
        return (
          formProps.onFinish &&
          formProps.onFinish({
            ...values,
            option_type: '',
            option_details: option_array,
            status: questionStatus,
            reviewer_id: reviewerId,
            approver_id: approverId,
          })
        )
      } else {
        return (
          formProps.onFinish &&
          formProps.onFinish({
            ...values,
            option_type: '',
            option_details: values?.option_details,
            status: questionStatus,
            reviewer_id: reviewerId,
            approver_id: approverId,
          })
        )
      }
    }
  }

  useEffect(() => {
    if (record && record.question_type_id) {
      setQuestionType(record.question_type_id)
    }
    if (record && record?.option_details) {
      formProps.form?.setFieldsValue({ options_details: record?.option_details })
    }
  }, [record])

  useEffect(() => {
    setTaxonomyFilter([
      {
        field: 'competency_id',
        operator: 'eq',
        value: record?.competency_id ?? '5d8c5987-c185-4bd6-a5c1-2dae0a69501d',
      },
    ])
    let strandfilter = []
    if (record?.taxonomy_id) {
      strandfilter.push({
        field: 'taxonomy_id',
        operator: 'eq',
        value: record?.taxonomy_id,
      })
    }
    if (record?.competency_id) {
      strandfilter.push({
        field: 'competency_id',
        operator: 'eq',
        value: record?.competency_id,
      })
    }
    if (record?.teacher_level_id) {
      strandfilter.push({
        field: 'teacher_level_id',
        operator: 'eq',
        value: record?.teacher_level_id,
      })
      setStrandFilter(strandfilter)
    }
  }, [record])

  useEffect(() => {
    if (record?.status) setQuestionStatus(record?.status)
    if (record?.option_type) setOptionType(record?.option_type)
  }, [record])

  return (
    <Modal {...modalProps} title="Edit Question" okButtonProps={{ hidden: true }} cancelButtonProps={{ hidden: true }} style={{height:"100%",top:0}}>
      {JSON.stringify(record) !== '{}' && record?.id !== '' ? (
        <>
          <Descriptions size="small" column={1}>
            <Descriptions.Item label="">{<Tag color="blue">{record?.status}</Tag>}</Descriptions.Item>
          </Descriptions>

          <Descriptions size="small" column={3}>
            {record?.subject?.name && <Descriptions.Item label="Subject">{record?.subject?.name}</Descriptions.Item>}
            {/* {record?.grade?.name && <Descriptions.Item label="Grade">{record?.grade?.name}</Descriptions.Item>} */}
            {record?.theme?.name && <Descriptions.Item label="Theme">{record?.theme?.name}</Descriptions.Item>}
            {record?.sub_theme?.name && <Descriptions.Item label="Sub Theme">{record?.sub_theme?.name}</Descriptions.Item>}
            {record?.topic?.name && <Descriptions.Item label="Topic">{record?.topic?.name}</Descriptions.Item>}
            {record?.question_type?.name && <Descriptions.Item label="Question Type">{record?.question_type?.name}</Descriptions.Item>}
          </Descriptions>
          <Divider style={{ margin: '4px 0' }} />
          {(role === 'qreviewer' || role === 'qapprover') && (
            <>
              <Descriptions size="small" column={2}>
                {record?.competencies?.name && <Descriptions.Item label="Competency">{record?.competencies?.name}</Descriptions.Item>}
                {record?.taxonomy?.name && <Descriptions.Item label="TN Taxonomy">{record?.taxonomy?.name}</Descriptions.Item>}
                {record?.teacher_level?.name && <Descriptions.Item label="Teacher Level">{record?.teacher_level?.name}</Descriptions.Item>}
                {record?.strand?.name && <Descriptions.Item label="Strand">{record?.strand?.name}</Descriptions.Item>}
                {record?.bloom_level?.name && <Descriptions.Item label="Bloom Level">{record?.bloom_level?.name}</Descriptions.Item>}
                {record?.difficulty_level?.name && <Descriptions.Item label="Difficulty">{record?.difficulty_level?.name}</Descriptions.Item>}
                {record?.report_theme?.name && <Descriptions.Item label="Report Theme">{record?.report_theme?.name}</Descriptions.Item>}
                {record?.centa_concept?.name && <Descriptions.Item label="Centa Concept">{record?.centa_concept?.name}</Descriptions.Item>}
              </Descriptions>
              <Divider style={{ margin: '4px 0' }} />
            </>
          )}
        </>
      ) : null}
      <Form
        {...formProps}
        layout="vertical"
        onFinish={values => {
          onFinish(values).then(() => updatePercentage())
        }}
      >
        <Row gutter={[16, 0]}>
          {role !== 'qreviewer' && role !== 'qapprover' && (
            <Col xs={24} lg={8}>
              <Form.Item label="Grade" name="grade_ids">
                {gradesToPush ? gradeQueryResult?.data?.data?.map(({ id, name }) => <Typography.Text keyboard>{name}</Typography.Text>) : <></>}
              </Form.Item>
            </Col>
          )}
        </Row>
        {/* <Row gutter={[16, 0]}>
          <Col xs={24} lg={8}>
            <Form.Item label="Reviewer" name="reviewer_id" rules={[{ required: false }]}>
              <Select>
                {qreviewewList.length > 0 &&
                  qreviewewList?.map(item => (
                    <Select.Option key={item?.user?.id} value={item?.user?.id}>
                      {item?.user?.displayName}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} lg={8}>
            <Form.Item label="Approver" name="approver_id" rules={[{ required: false }]}>
              <Select>
                {qapproverList.length > 0 &&
                  qapproverList?.map(item => (
                    <Select.Option key={item?.user?.id} value={item?.user?.id}>
                      {item?.user?.displayName}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
        </Row> */}

        <Row gutter={[16, 0]}>
          <Col xs={24} lg={24}>
            <Form.Item
              label="Question"
              name="title"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <>
                <SunTextEditor
                  id="question_title_edit"
                  initialValue={record?.title}
                  formValueSetterFn={(content: string) => {
                    formProps?.form?.setFieldsValue({ title: content })
                  }}
                />
              </>
            </Form.Item>
          </Col>
          {questionType === 'ShortAnswer' && (
            <Col xs={24} lg={24}>
              <Form.Item label="Expected points in the answer" name="description">
                <Input />
              </Form.Item>
            </Col>
          )}
        </Row>
        <Row gutter={[16, 0]}>
          <Col xs={24} lg={24}>
            <Divider orientation="left" orientationMargin="0" className="primary">
              {questionType === 'ShortAnswer' && 'Rubric Details'}
              {questionType === 'MCQ' && 'Option Details'}
              {questionType === 'Match' && 'Label Details'}
              {questionType === 'Fill' && 'Answer Details'}
            </Divider>
          </Col>
          {/* {questionType === ('MCQ' || 'Match') && role !== 'qreviewer' && role !== 'qapprover' && ( */}
          {questionType === 'MCQ' && role !== 'qreviewer' && role !== 'qapprover' && (
            <Col xs={24} lg={12}>
              <Form.Item label="Options Type" name="option_type" initialValue={'Text'}>
                <Select
                  onChange={handleOptionType}
                  options={[
                    { label: 'Text', value: 'Text' },
                    { label: 'Image', value: 'Image' },
                  ]}
                />
              </Form.Item>
            </Col>
          )}
        </Row>
        {questionType === 'MCQ' && (
          <Form.List name="option_details">
            {(fields, { add, remove }) => {
              if (Object.keys(fileList)?.length === 0 && fields?.length > 0) {
                let arr: any = {}
                for (let i = 0; i < fields?.length; i++) {
                  arr[i] = []
                }
                setFileList(arr)
              }
              {
                // console.log('fileList', record?.option_details)
              }
              return (
                <>
                  {fields.map(({ key, name, ...restField }) => {
                    return (
                      <Row gutter={[8, 16]} style={{ display: 'flex', alignItems: 'center' }}>
                        <Col lg={10}>
                          <Form.Item {...restField} name={[name, 'option_text']} rules={[{ required: true, message: 'Missing Option Value' }]}>
                            {/* <ReactMde
                              readOnly={
                                (userRole === 'qreviewer' && record?.second_reviewer_id === user?.id) ||
                                (userRole === 'qapprover' && user?.id === record?.second_approver_id)
                              }
                              selectedTab={selectedOptionTab?.includes('mcq_' + key) ? 'preview' : 'write'}
                              onTabChange={(value: any) => handleOptionTabChange(value, 'mcq_' + key)}
                              maxEditorHeight={85}
                              onChange={value => handleValueChange(value, key)}
                              generateMarkdownPreview={markdown => Promise.resolve(<ReactMarkdown>{markdown}</ReactMarkdown>)}
                              toolbarCommands={ReactMdeToolbarCommands}
                              paste={{
                                saveImage: saveOptions,
                              }}
                            /> */}
                            <>
                            
                              {/* <RichTextEditor
                                id={`${name}option_text`}
                                hideToolBarAndMenu={
                                  (userRole === 'qreviewer' && record?.second_reviewer_id === user?.id) ||
                                  (userRole === 'qapprover' && user?.id === record?.second_approver_id)
                                }
                                initialValue={record?.option_details?.[key]?.['option_text'] ?? ''}
                                formValueSetterFn={(content: string) => {
                                  handleSetFormList(content, key, 'option_text')
                                }}
                                // handleImageUpload={handleImageUplodFromEditor}
                              /> */}
                              {/* ------------------------------------------------ */}
                              <SunTextEditor
                                id={`${name}option_text`}
                                hideToolBar={
                                  (userRole === 'qreviewer' && record?.second_reviewer_id === user?.id) ||
                                  (userRole === 'qapprover' && user?.id === record?.second_approver_id)
                                }
                                initialValue={record?.option_details?.[key]?.['option_text'] ?? ''}
                                formValueSetterFn={(content: string) => {
                                  handleSetFormList(content, key, 'option_text')
                                }}
                                // handleImageUpload={handleImageUplodFromEditor}
                              />
                            </>
                          </Form.Item>
                        </Col>
                        <Col lg={5}>
                          <Form.Item {...restField} name={[name, 'option_insight']} rules={[{ required: false }]}>
                            <TextArea
                              rows={7}
                              placeholder="Option Insight"
                              disabled={
                                (userRole === 'qreviewer' && record?.second_reviewer_id === user?.id) ||
                                (userRole === 'qapprover' && user?.id === record?.second_approver_id)
                              }
                            />
                          </Form.Item>
                        </Col>
                        <Col lg={4}>
                          <Form.Item {...restField} name={[name, 'option_flag']} rules={[{ required: true, message: 'Missing Option Flag' }]}>
                            <Select
                              placeholder="Option Flag"
                              style={{ width: 150 }}
                              disabled={
                                (userRole === 'qreviewer' && record?.second_reviewer_id === user?.id) ||
                                (userRole === 'qapprover' && user?.id === record?.second_approver_id)
                              }
                              // value={selectedItems}
                              onChange={handleOptionTags}
                              // onDeselect={handleDeselectOptionTags}
                              allowClear
                              options={filteredOptions.map(item => ({
                                value: item,
                                label: item,
                              }))}
                            />
                          </Form.Item>{' '}
                        </Col>
                        {userRole === 'qreviewer' && (
                          <Col lg={4}>
                            <Form.Item
                              {...restField}
                              name={[name, 'reviewer_comments']}
                              rules={[{ required: false, message: 'Missing Option Comments' }]}
                            >
                              <TextArea
                                rows={7}
                                disabled={userRole === 'qreviewer' && record?.second_reviewer_id === user?.id}
                                placeholder="Reviewer Comments"
                              />

                              {/* <Input placeholder="Reviewer Comments" /> */}
                            </Form.Item>
                          </Col>
                        )}
                        {userRole === 'qapprover' && (
                          <Col lg={4}>
                            <Form.Item
                              {...restField}
                              name={[name, 'approver_comments']}
                              rules={[{ required: false, message: 'Missing Option Comments' }]}
                            >
                              <TextArea
                                rows={7}
                                placeholder="Approver Comments"
                                disabled={userRole === 'qapprover' && user?.id === record?.second_approver_id}
                              />
                            </Form.Item>
                          </Col>
                        )}
                        <Col lg={1}>
                          <MinusCircleOutlined onClick={() => remove(name)} />
                        </Col>
                      </Row>
                    )
                  })}

                  <Form.Item>
                    <Button
                      disabled={
                        (userRole === 'qreviewer' && record?.second_reviewer_id === user?.id) ||
                        (userRole === 'qapprover' && user?.id === record?.second_approver_id)
                      }
                      type="dashed"
                      onClick={() => {
                        add()
                      }}
                      block
                      icon={<PlusOutlined />}
                    >
                      Add Option
                    </Button>
                  </Form.Item>
                </>
              )
            }}
          </Form.List>
        )}

        {errorOption === true ? <Typography.Text type="danger">Select atleast one Right Answer </Typography.Text> : null}
        {questionType === 'Fill' && (
          <Form.List name="option_details">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item {...restField} name={[name, 'right_answer']} rules={[{ required: true, message: 'Missing Option Answer' }]}>
                      {/* <Input placeholder="Right Answer" type="small" style={{ minWidth: 300 }} /> */}
                      <SunTextEditor
                        id={`edit_option_details_fill_right_answer${name}`}
                        key={key}
                        formValueSetterFn={content => {
                          handleSetFormList(content, key, 'right_answer')
                        }}
                      />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'option_insights']} rules={[{ required: false }]}>
                      <Input placeholder="Option Insight" type="small" />
                    </Form.Item>
                    {role === 'qreviewer' && (
                      <Form.Item {...restField} name={[name, 'reviewer_comments']} rules={[{ required: false, message: 'Missing Option Comments' }]}>
                        <Input placeholder="Reviewer Comments" />
                      </Form.Item>
                    )}
                    {role === 'qapprover' && (
                      <Form.Item {...restField} name={[name, 'approver_comments']} rules={[{ required: false, message: 'Missing Option Comments' }]}>
                        <Input placeholder="Approver Comments" />
                      </Form.Item>
                    )}
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Space>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Option
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        )}
        {questionType === 'Match' && (
          <Form.List name="option_details">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    {optionType === 'Text' && (
                      <>
                        <Form.Item {...restField} name={[name, 'left_label_text']} rules={[{ required: true, message: 'Missing Section' }]}>
                          <Input placeholder="Left Label" type="small" />
                        </Form.Item>
                        <Form.Item {...restField} name={[name, 'right_label_text']} rules={[{ required: true, message: 'Missing Section' }]}>
                          <Input placeholder="Right Label" type="small" />
                        </Form.Item>
                      </>
                    )}
                    {optionType === 'Image' && (
                      <>
                        <Form.Item {...restField} name={[name, 'left_label_image']} rules={[{ required: true, message: 'Missing Section' }]}>
                          {/* <Upload {...uploadprops}>
                            <Button icon={<UploadOutlined />}>Left Label Image</Button>
                          </Upload> */}
                        </Form.Item>
                        <Form.Item {...restField} name={[name, 'right_label_image']} rules={[{ required: true, message: 'Missing Section' }]}>
                          {/* <Upload {...uploadprops}>
                            <Button icon={<UploadOutlined />}>Right Label Image</Button>
                          </Upload> */}
                        </Form.Item>
                      </>
                    )}
                    <Form.Item {...restField} name={[name, 'option_insight']} rules={[{ required: false }]}>
                      <Input placeholder="Option Insight" type="small" />
                    </Form.Item>
                    {role === 'qreviewer' && (
                      <Form.Item {...restField} name={[name, 'reviewer_comments']} rules={[{ required: false, message: 'Missing Option Comments' }]}>
                        <Input placeholder="Reviewer Comments" />
                      </Form.Item>
                    )}
                    {role === 'qapprover' && (
                      <Form.Item {...restField} name={[name, 'approver_comments']} rules={[{ required: false, message: 'Missing Option Comments' }]}>
                        <Input placeholder="Approver Comments" />
                      </Form.Item>
                    )}
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Space>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Option
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        )}
        {questionType === 'ShortAnswer' && (
          <Form.List
            initialValue={[
              { component_name: '', rating_1: '', rating_2: '', rating_3: '', rating_4: '', weightage: '' },
              { component_name: '', rating_1: '', rating_2: '', rating_3: '', rating_4: '', weightage: '' },
              { component_name: '', rating_1: '', rating_2: '', rating_3: '', rating_4: '', weightage: '' },
            ]}
            rules={[
              {
                validator: async (_, names) => {
                  if (names?.length >= 8) {
                    return Promise.reject(new Error('Exceeded maximum rubric field. (Max is 7)'))
                  }
                },
              },
            ]}
            name="option_details"
          >
            {(fields, { add, remove }, { errors }) => {
              return (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                      <Form.Item {...restField} name={[name, 'component_name']} rules={[{ required: true, message: 'Missing Section' }]}>
                        <Input placeholder="Component Name" type="small" />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, 'rating_1']} rules={[{ required: true, message: 'Missing Rating 1' }]}>
                        <Input placeholder="Rating 1" type="small" />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, 'rating_2']} rules={[{ required: true, message: 'Missing Rating 2' }]}>
                        <Input placeholder="Rating 2" type="small" />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, 'rating_3']} rules={[{ required: true, message: 'Missing Rating 3' }]}>
                        <Input placeholder="Rating 3" type="small" />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, 'rating_4']} rules={[{ required: true, message: 'Missing Rating 4' }]}>
                        <Input placeholder="Rating 4" type="small" />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, 'weightage']} rules={[{ required: true, message: 'Missing Weightage' }]}>
                        <Input placeholder="Weightage" type="small" style={{ maxWidth: 150 }} />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(name)} />
                    </Space>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Add Option
                    </Button>
                  </Form.Item>
                  <Form.ErrorList errors={errors} />
                </>
              )
            }}
          </Form.List>
        )}
        {role !== 'qreviewer' && role !== 'qapprover' && (
          <Row gutter={[16, 0]}>
            {/* <Col xs={24} lg={24}>
              <Divider orientation="left" orientationMargin="0" className="primary">
                Tags
              </Divider>
            </Col>
            <Col xs={24} lg={16}>
              <Form.Item label="Competency" name="competency_id" rules={[{ required: true }]}>
                <Select onChange={handleCompetencyChange} {...competencySelectProps} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item label="TN Taxonomy" name="tn_taxonomy_id" rules={[{ required: true }]}>
                <Select onChange={handleTaxonomyChange} {...tntaxonomySelectProps} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item label="Teacher Level" name="teacher_level_id" rules={[{ required: true }]}>
                <Select onChange={handleTeacherLevelChange} {...teacherlevelSelectProps} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item label="Strand" name="strand_id" rules={[{ required: true }]}>
                <Select {...strandSelectProps} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item label="Blooms Level" name="bloom_level_id" rules={[{ required: true }]}>
                <Select {...bloomlevelSelectProps} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item label="Difficulty Level" name="difficulty_level_id" rules={[{ required: true }]}>
                <Select {...difficultylevelSelectProps} />
              </Form.Item>
            </Col> */}
            {/* 
            <Col xs={24} lg={8}>
              <Form.Item label="Report Theme" name="report_theme_id" rules={[{ required: true }]}>
                <Select {...reportThemeProps} />
              </Form.Item>
            </Col>
       
            <Col xs={24} lg={8}>
              <Form.Item label="Centa Concept" name="centa_concept_id" rules={[{ required: true }]}>
                <Select {...centaConceptProps} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item label="Explanation Type" name="explanation_type_id" rules={[{ required: true }]}>
                <Select {...explanationTypeProps} />
              </Form.Item>
            </Col> */}

            <Form.Item name="created_by" initialValue={user?.id}>
              <Input type="hidden" />
            </Form.Item>
          </Row>
        )}
        <Divider />
        <Row gutter={[16, 0]} justify="end" align="top">
          <Col xs={24} lg={8} style={{ display: 'flex', justifyContent: 'end' }}>
            <Space>
              <Button
                onClick={() => {
                  formProps?.form?.resetFields()
                  setFileList([])
                  close()
                }}
              >
                Cancel
              </Button>
              {role === 'qcreator' && (
                <Button
                  htmlType="submit"
                  onClick={() => {
                    handleOnFinish('For Review')
                  }}
                  disabled={(questionStatus !== 'Open' && questionStatus !== 'Open (Require Amending)') || reviewers?.length < 1}
                >
                  Submit for Review
                </Button>
              )}
              {role === 'qreviewer' && (
                <>
                  <Button
                    htmlType="submit"
                    onClick={() => {
                      handleOnFinish('Rejected at Review')
                    }}
                    disabled={questionStatus !== 'For Review' && questionStatus !== 'For Review (Require Amending)'}
                  >
                    Reject
                  </Button>
                  <Button
                    htmlType="submit"
                    onClick={() => {
                      handleOnFinish('Open (Require Amending)')
                    }}
                    disabled={questionStatus !== 'For Review' && questionStatus !== 'For Review (Require Amending)'}
                  >
                    Require Amending
                  </Button>
                  <Button
                    htmlType="submit"
                    onClick={() => {
                      handleOnFinish('For Approval')
                    }}
                    disabled={(questionStatus !== 'For Review' && questionStatus !== 'For Review (Require Amending)') || approvers?.length < 1}
                  >
                    Submit for Approval
                  </Button>
                </>
              )}
              {role === 'qapprover' && (
                <>
                  <Button
                    htmlType="submit"
                    onClick={() => {
                      handleOnFinish('Rejected at Approval')
                    }}
                    disabled={questionStatus !== 'For Approval'}
                  >
                    Reject
                  </Button>
                  <Button
                    htmlType="submit"
                    onClick={() => {
                      handleOnFinish('For Review (Require Amending)')
                    }}
                    disabled={questionStatus !== 'For Approval'}
                  >
                    Require Amending
                  </Button>
                  <Button
                    htmlType="submit"
                    onClick={() => {
                      handleOnFinish('Published')
                    }}
                    disabled={questionStatus !== 'For Approval'}
                  >
                    Approve
                  </Button>
                </>
              )}
              <Button type="primary" htmlType="submit">
                Save
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
